<template>
  <div class="usage-dashboard space-y-6">
    <!-- Header & Controls -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-5 rounded-2xl border border-gray-100 shadow-sm">
      <div>
        <h2 class="text-2xl font-black text-gray-900 tracking-tight flex items-center gap-2.5">
          <span class="p-2 bg-emerald-100 text-emerald-700 rounded-xl text-lg">🚜</span>
          Equipment Usage & Operating Analytics
        </h2>
        <p class="text-xs text-gray-500 mt-1">
          Detailed metrics of machinery operating hours, fuel consumed, operational fuel costs, and efficiency.
        </p>
      </div>

      <div class="flex items-center gap-2.5 w-full sm:w-auto">
        <!-- Date range buttons -->
        <div class="flex bg-gray-100 p-1 rounded-xl text-xs font-semibold">
          <button 
            v-for="preset in presets" 
            :key="preset.days"
            @click="selectPreset(preset.days)"
            class="px-3 py-1.5 rounded-lg transition-all"
            :class="selectedDays === preset.days ? 'bg-white text-emerald-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'"
          >
            {{ preset.label }}
          </button>
        </div>

        <button 
          @click="fetchReport" 
          :disabled="loading"
          class="p-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-colors disabled:opacity-50"
          title="Refresh Report"
        >
          <svg class="w-4 h-4" :class="{ 'animate-spin': loading }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>
    </div>

    <!-- KPI Summary Cards -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <!-- Total Hours -->
      <div class="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm relative overflow-hidden">
        <div class="flex items-center justify-between">
          <span class="text-xs font-bold uppercase tracking-wider text-gray-400">Operating Hours</span>
          <span class="p-2 bg-blue-50 text-blue-600 rounded-xl text-sm">⏱️</span>
        </div>
        <div class="mt-3">
          <span class="text-2xl font-black text-gray-900">{{ (report?.total_hours_used || 0).toLocaleString() }}</span>
          <span class="text-xs font-semibold text-gray-500 ml-1">hrs</span>
        </div>
        <p class="text-xs text-gray-400 mt-1">Total recorded machine runtime</p>
      </div>

      <!-- Fuel Consumed -->
      <div class="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm relative overflow-hidden">
        <div class="flex items-center justify-between">
          <span class="text-xs font-bold uppercase tracking-wider text-gray-400">Fuel Consumed</span>
          <span class="p-2 bg-amber-50 text-amber-600 rounded-xl text-sm">⛽</span>
        </div>
        <div class="mt-3">
          <span class="text-2xl font-black text-gray-900">{{ (report?.total_fuel_consumed || 0).toLocaleString() }}</span>
          <span class="text-xs font-semibold text-gray-500 ml-1">liters</span>
        </div>
        <p class="text-xs text-gray-400 mt-1">Operational fuel burned in operations</p>
      </div>

      <!-- Operational Fuel Cost -->
      <div class="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm relative overflow-hidden">
        <div class="flex items-center justify-between">
          <span class="text-xs font-bold uppercase tracking-wider text-gray-400">Operational Fuel Cost</span>
          <span class="p-2 bg-emerald-50 text-emerald-600 rounded-xl text-sm">💰</span>
        </div>
        <div class="mt-3">
          <span class="text-2xl font-black text-emerald-700">TZS {{ (report?.total_operational_fuel_cost || 0).toLocaleString() }}</span>
        </div>
        <p class="text-xs text-emerald-600 font-medium mt-1">Calculated using active market rates</p>
      </div>

      <!-- Fuel Burn Rate -->
      <div class="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm relative overflow-hidden">
        <div class="flex items-center justify-between">
          <span class="text-xs font-bold uppercase tracking-wider text-gray-400">Avg Burn Rate</span>
          <span class="p-2 bg-purple-50 text-purple-600 rounded-xl text-sm">⚡</span>
        </div>
        <div class="mt-3">
          <span class="text-2xl font-black text-gray-900">{{ report?.avg_liters_per_hour || 0 }}</span>
          <span class="text-xs font-semibold text-gray-500 ml-1">L / hr</span>
        </div>
        <p class="text-xs text-gray-400 mt-1">Average hourly fuel consumption</p>
      </div>
    </div>

    <!-- Equipment Breakdown Table -->
    <div class="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div class="p-5 border-b border-gray-100 flex justify-between items-center">
        <div>
          <h3 class="text-base font-bold text-gray-900">Machine-by-Machine Usage Breakdown</h3>
          <p class="text-xs text-gray-500 mt-0.5">Operating hours, fuel volume, and operational costs per equipment</p>
        </div>
        <span class="text-xs font-semibold text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
          {{ report?.by_equipment?.length || 0 }} Machines
        </span>
      </div>

      <div v-if="loading" class="p-8 text-center text-sm text-gray-400">
        Loading equipment usage analytics...
      </div>

      <div v-else-if="!report?.by_equipment?.length" class="p-12 text-center text-gray-400">
        <span class="text-3xl block mb-2">🚜</span>
        <p class="font-medium text-gray-600">No equipment usage logged for this period.</p>
        <p class="text-xs text-gray-400 mt-1">Usage records logged with hours and fuel will display here with automated costs.</p>
      </div>

      <div v-else class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-100 text-sm text-left">
          <thead class="bg-gray-50 text-xs font-bold text-gray-500 uppercase tracking-wider">
            <tr>
              <th class="px-5 py-3.5">Equipment</th>
              <th class="px-5 py-3.5">Category</th>
              <th class="px-5 py-3.5 text-center">Fuel Type</th>
              <th class="px-5 py-3.5 text-right">Hours Used</th>
              <th class="px-5 py-3.5 text-right">Fuel Used (L)</th>
              <th class="px-5 py-3.5 text-right">Burn Rate</th>
              <th class="px-5 py-3.5 text-right">Operational Cost</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-50 bg-white">
            <tr v-for="eq in report.by_equipment" :key="eq.equipment_id" class="hover:bg-emerald-50/20 transition-colors">
              <td class="px-5 py-4 font-bold text-gray-900 flex items-center gap-2">
                <span class="w-2 h-2 rounded-full bg-emerald-500"></span>
                {{ eq.equipment_name }}
              </td>
              <td class="px-5 py-4 text-xs text-gray-500">
                {{ eq.equipment_type }}
              </td>
              <td class="px-5 py-4 text-center">
                <span class="px-2 py-0.5 rounded-full text-xs font-medium" :class="eq.fuel_type ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-gray-100 text-gray-600'">
                  {{ eq.fuel_type || 'N/A' }}
                </span>
              </td>
              <td class="px-5 py-4 text-right font-semibold text-gray-800">
                {{ eq.hours_used.toLocaleString() }} hrs
              </td>
              <td class="px-5 py-4 text-right font-semibold text-gray-800">
                {{ eq.fuel_consumed.toLocaleString() }} L
              </td>
              <td class="px-5 py-4 text-right text-xs text-gray-600">
                {{ eq.liters_per_hour }} L/hr
              </td>
              <td class="px-5 py-4 text-right font-bold text-emerald-700">
                TZS {{ eq.fuel_cost.toLocaleString() }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Recent Logs Table -->
    <div v-if="report?.recent_logs?.length" class="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div class="p-5 border-b border-gray-100">
        <h3 class="text-base font-bold text-gray-900">Recent Operational Logs & Field Tasks</h3>
        <p class="text-xs text-gray-500 mt-0.5">Individual equipment activity records with automated rate calculation</p>
      </div>

      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-100 text-sm text-left">
          <thead class="bg-gray-50 text-xs font-bold text-gray-500 uppercase tracking-wider">
            <tr>
              <th class="px-5 py-3">Date</th>
              <th class="px-5 py-3">Machine</th>
              <th class="px-5 py-3 text-right">Hours</th>
              <th class="px-5 py-3 text-right">Fuel Used</th>
              <th class="px-5 py-3 text-right">Rate / Liter</th>
              <th class="px-5 py-3 text-right">Fuel Cost</th>
              <th class="px-5 py-3">Tasks Performed</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-50 bg-white">
            <tr v-for="log in report.recent_logs" :key="log.id" class="hover:bg-gray-50/50">
              <td class="px-5 py-3 text-xs font-medium text-gray-600">{{ log.date }}</td>
              <td class="px-5 py-3 font-semibold text-gray-900">{{ log.equipment_name }}</td>
              <td class="px-5 py-3 text-right text-gray-700">{{ log.hours_used }} hrs</td>
              <td class="px-5 py-3 text-right text-gray-700">{{ log.fuel_used }} L</td>
              <td class="px-5 py-3 text-right text-xs text-gray-500">TZS {{ log.cost_per_liter.toLocaleString() }}</td>
              <td class="px-5 py-3 text-right font-bold text-emerald-700">TZS {{ log.fuel_cost.toLocaleString() }}</td>
              <td class="px-5 py-3 text-xs text-gray-600 max-w-xs truncate">{{ log.tasks_performed || '-' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { usageApi } from '@/api/usageApi'
import type { EquipmentUsageReport } from '@/types/usage'

const loading = ref(false)
const selectedDays = ref<number | string>(30)
const report = ref<EquipmentUsageReport | null>(null)

const presets = [
  { label: '7 Days', days: 7 },
  { label: '30 Days', days: 30 },
  { label: '90 Days', days: 90 },
  { label: 'All Time', days: 365 },
]

const selectPreset = (days: number | string) => {
  selectedDays.value = days
  fetchReport()
}

const fetchReport = async () => {
  try {
    loading.value = true
    report.value = await usageApi.getUsageReport({ days: selectedDays.value })
  } catch (err) {
    console.error('Failed to load usage report', err)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchReport()
})
</script>
